/**
 * Client mirror of the server's NPI gate (`server/app/lib/npi.py::valid_npi`).
 *
 * The server re-validates and is the authority (a 422 on a bad NPI). These
 * checks gate a submit or lookup button and show an inline error so the user
 * doesn't round-trip to the 422.
 */

const NPI_RE = /^\d{10}$/;

/**
 * The NPI's 10th digit is a Luhn check digit computed over the health-industry
 * prefix `"80840"` followed by the first 9 digits. Assumes a 10-digit numeric
 * string, since callers gate on `NPI_RE` first. Returns true iff the check
 * digit matches.
 */
function npiChecksumValid(npi: string): boolean {
  const payload = "80840" + npi.slice(0, 9);
  let total = 0;
  for (let i = 0; i < payload.length; i++) {
    // Double every other digit counting from the right (the rightmost is even-position).
    const fromRight = payload.length - 1 - i;
    let digit = Number(payload[i]);
    if (fromRight % 2 === 0) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    total += digit;
  }
  const check = (10 - (total % 10)) % 10;
  return check === Number(npi[9]);
}

/**
 * Validates an NPI field. Returns an error message when invalid, or `null`
 * when the NPI is acceptable.
 *
 * A blank NPI fails as required, a present one must be 10 digits, and a
 * 10-digit one must pass the checksum, each with its own message.
 */
export function validateNpi(npi: string): string | null {
  const trimmed = npi.trim();
  if (!trimmed) {
    return "NPI is required.";
  }
  if (!NPI_RE.test(trimmed)) {
    return "NPI must be 10 digits.";
  }
  if (!npiChecksumValid(trimmed)) {
    return "That isn't a valid NPI — please check the number.";
  }
  return null;
}
